// analysis/aps/apsFetchData.jsx

import { API_BASE_URL } from "../../utils/Constants";
import { ultimoAnalisis } from "../../utils/mapUtils";
import { obtenerDatosCompletosAps } from "../../utils/Constants";
import { API_BASE_URL_DASHBOARD } from "../../utils/config";

/**
 * Convierte la respuesta de `dashboard/aps/completo` (o de un endpoint con el
 * mismo contrato, como Herbicidas) en el objeto de indicadores de las tarjetas.
 */
export const buildApsDatosAnalisis = (data) => {
    {
        const safeNumber = (v, decimals = 2) =>
            v != null && !isNaN(v) ? Number(v).toFixed(decimals) : '';

        const safeArray = (arr, decimals = 2) =>
            Array.isArray(arr)
                ? arr.map(v => safeNumber(v, decimals)).join(', ')
                : '';

        // ✅ Parse seguro (por si backend manda string JSON)
        const safeJsonParse = (raw) => {
            try {
                return JSON.parse(raw);
            } catch {
                return null;
            }
        };

        const parseMaybeJson = (val) => {
            if (val == null) return val;
            if (typeof val !== 'string') return val;
            const t = val.trim();
            if (!t) return val;

            const looksJson =
                (t.startsWith('{') && t.endsWith('}')) ||
                (t.startsWith('[') && t.endsWith(']'));

            if (!looksJson) return val;

            const parsed = safeJsonParse(t);
            return parsed != null ? parsed : val;
        };

        /* ======================================================================
           ✅ NUEVO (MEJORADO): Normaliza LISTA_KMLS_IDS_LOTE preservando propiedades
           Soporta:
           1) [{KML:1, IDS_LOTES:["..."]}] o [{kml, ids}]
           2) [{KML:1, LOTES:[{ID,LOTE,N_FINCA,C_FINCA,TERCIO,ZAFRA}, ...]}]
           3) {"1":[...ids...]}
           4) {"1":[{...loteObj...}, ...]}
           5) String JSON de cualquiera de los anteriores
           Devuelve: [{ kml, lotes:[{ID,LOTE,N_FINCA,C_FINCA,TERCIO,ZAFRA}], ids:[...] }]
           ====================================================================== */

        const asTrim = (v) => String(v ?? '').trim();

        const pickFirst = (obj, keys) => {
            if (!obj || typeof obj !== 'object') return '';
            for (const k of keys) {
                if (k in obj && obj[k] != null && asTrim(obj[k]) !== '') return obj[k];
            }
            return '';
        };

        const normalizeLoteObj = (obj) => {
            if (!obj || typeof obj !== 'object') return null;

            const id = pickFirst(obj, [
                'ID', 'id', '_id',
                'ID_LOTE', 'id_lote', 'lote_id',
                'ID_POLIGONO', 'ID_LOTE_POLIGONO',
                'IDS_LOTE', 'IDS_LOTES', 'IDS', 'ids'
            ]);

            const lote = pickFirst(obj, [
                'LOTE', 'lote', 'NUM_LOTE', 'N_LOTE', 'LOTE_NUM', 'NUMERO_LOTE',
                'LOTES'
            ]);

            const nFinca = pickFirst(obj, [
                'N. FINCA', 'N FINCA', 'N_FINCA', 'NFINCA',
                'N_FINCAS', 'NFINCAS',
                'NOMBRE_FINCA', 'Nombre Finca', 'nombreFinca'
            ]);

            const cFinca = pickFirst(obj, [
                'C.FINCA', 'C FINCA', 'C_FINCA', 'CFINCA',
                'C_FINCAS', 'CFINCAS',
                'CODIGO_FINCA', 'codigoFinca'
            ]);

            const tercio = pickFirst(obj, ['TERCIO', 'tercio']);
            const zafra  = pickFirst(obj, ['ZAFRA', 'zafra']);

            const out = {
                ID: asTrim(id),
                LOTE: asTrim(lote),
                N_FINCA: asTrim(nFinca),
                C_FINCA: asTrim(cFinca),
                TERCIO: asTrim(tercio),
                ZAFRA: asTrim(zafra),
            };

            const hasSomething = Object.values(out).some(v => v !== '');
            return hasSomething ? out : null;
        };


        const normalizeListadoIdsLoteKmls = (raw) => {
            const parsed = parseMaybeJson(raw);
            if (parsed == null || parsed === '') return [];

            const normalizeEntry = (item, idxKmlFallback) => {
                const toStrArr = (v) => {
                    if (Array.isArray(v)) return v.map(asTrim).filter(Boolean);
                    const s = asTrim(v);
                    return s ? [s] : [];
                };

                const uniq = (arr) => Array.from(new Set((arr || []).map(asTrim).filter(Boolean)));

                // item primitivo => tratamos como ID único
                if (!item || typeof item !== 'object') {
                    const asId = asTrim(item);
                    return {
                        kml: idxKmlFallback,
                        lotes: asId
                            ? [{ ID: asId, LOTE: '', N_FINCA: '', C_FINCA: '', TERCIO: '', ZAFRA: '' }]
                            : [],
                        ids: asId ? [asId] : [],
                        // extras para compatibilidad con filtros
                        IDS: asId ? [asId] : [],
                        IDS_LOTES: asId ? [asId] : [],
                        LOTES: [],
                        N_FINCAS: [],
                        C_FINCAS: [],
                    };
                }

                // KML
                const kmlRaw = item.kml ?? item.KML ?? item.numeroKml ?? item.numero ?? item.index ?? idxKmlFallback;
                const kmlNum = Number(kmlRaw);
                const kml = !isNaN(kmlNum) ? kmlNum : idxKmlFallback;

                // Arrays “padre” (vienen en tu backend)
                const parentIds = toStrArr(
                    item.IDS_LOTES ??
                    item.IDS_LOTE ??
                    item.IDS ??
                    item.ids ??
                    item.ids_lotes ??
                    item.idPoligonos ??
                    item.poligonos ??
                    item.idsLote ??
                    item.ids_lote
                );

                const parentLotesNums = toStrArr(
                    item.LOTES ?? item.lotes ?? item.LOTE ?? item.lote
                );

                const parentNFincas = toStrArr(
                    item.N_FINCAS ?? item.n_fincas ?? item.N_FINCA ?? item['N. FINCA']
                );

                const parentCFincas = toStrArr(
                    item.C_FINCAS ?? item.c_fincas ?? item.C_FINCA ?? item['C. FINCA']
                );

                const parentTercios = toStrArr(item.TERCIOS ?? item.tercios ?? item.TERCIO ?? item.tercio);
                const parentZafras  = toStrArr(item.ZAFRAS ?? item.zafras ?? item.ZAFRA ?? item.zafra);

                // ✅ 1) Preferir LOTES_INFO si existe (y trae objetos)
                const lotesInfoRaw =
                    item.LOTES_INFO ??
                    item.lotes_info ??
                    item.lotesInfo ??
                    item.lotesDetalle ??
                    null;

                let lotes = [];

                if (Array.isArray(lotesInfoRaw) && lotesInfoRaw.some(x => x && typeof x === 'object' && !Array.isArray(x))) {
                    lotes = lotesInfoRaw.map(normalizeLoteObj).filter(Boolean);
                }

                // ✅ 2) Si LOTES_INFO no sirvió, pero sí tenemos LOTES (nums) y/o IDS (ids), alinearlos por índice
                if (!lotes.length) {
                    const n = Math.max(parentIds.length, parentLotesNums.length);
                    for (let i = 0; i < n; i++) {
                        const ID   = parentIds[i] ?? '';
                        const LOTE = parentLotesNums[i] ?? '';
                        const obj = {
                            ID: asTrim(ID),
                            LOTE: asTrim(LOTE),
                            N_FINCA: asTrim(parentNFincas[0] ?? ''),
                            C_FINCA: asTrim(parentCFincas[0] ?? ''),
                            TERCIO: asTrim(parentTercios[0] ?? ''),
                            ZAFRA: asTrim(parentZafras[0] ?? ''),
                        };
                        if (Object.values(obj).some(v => v !== '')) lotes.push(obj);
                    }
                }

                // ✅ 3) Si ya hay lotes (por LOTES_INFO), completar faltantes con parent arrays alineados
                if (lotes.length) {
                    for (let i = 0; i < lotes.length; i++) {
                        if (!asTrim(lotes[i].ID) && parentIds[i]) lotes[i].ID = asTrim(parentIds[i]);
                        if (!asTrim(lotes[i].LOTE) && parentLotesNums[i]) lotes[i].LOTE = asTrim(parentLotesNums[i]);

                        if (!asTrim(lotes[i].N_FINCA) && parentNFincas[0]) lotes[i].N_FINCA = asTrim(parentNFincas[0]);
                        if (!asTrim(lotes[i].C_FINCA) && parentCFincas[0]) lotes[i].C_FINCA = asTrim(parentCFincas[0]);

                        if (!asTrim(lotes[i].TERCIO) && parentTercios[0]) lotes[i].TERCIO = asTrim(parentTercios[0]);
                        if (!asTrim(lotes[i].ZAFRA) && parentZafras[0]) lotes[i].ZAFRA = asTrim(parentZafras[0]);
                    }
                }

                // ids finales
                let ids = parentIds.length ? parentIds : lotes.map(x => asTrim(x.ID)).filter(Boolean);
                if (!ids.length) ids = lotes.map(x => asTrim(x.ID)).filter(Boolean);

                // ✅ Extras para que tu DataSection pueda filtrar por finca/cfinca/lote sin depender solo del backend crudo
                const nfFromLotes = lotes.map(x => asTrim(x.N_FINCA)).filter(Boolean);
                const cfFromLotes = lotes.map(x => asTrim(x.C_FINCA)).filter(Boolean);
                const lotNumsFromLotes = lotes.map(x => asTrim(x.LOTE)).filter(Boolean);

                const N_FINCAS = uniq(parentNFincas.concat(nfFromLotes));
                const C_FINCAS = uniq(parentCFincas.concat(cfFromLotes));
                const LOTES    = uniq(parentLotesNums.concat(lotNumsFromLotes));

                return {
                    kml,
                    lotes,
                    ids,

                    // 👇 compatibilidad / filtros
                    IDS: ids,
                    IDS_LOTES: ids,
                    LOTES,
                    N_FINCAS,
                    C_FINCAS,
                };
            };


            // Caso: array
            if (Array.isArray(parsed)) {
                return parsed.map((item, idx) => normalizeEntry(item, idx + 1));
            }

            // Caso: objeto-mapa {"1":[...]} o {"1":{...}}
            if (parsed && typeof parsed === 'object') {
                const out = Object.entries(parsed).map(([k, v], idx) => {
                    const kmlNum = Number(k);
                    const kml = !isNaN(kmlNum) ? kmlNum : (idx + 1);

                    // v puede ser array de ids, array de objetos, o valor único
                    if (Array.isArray(v)) {
                        // objetos => LOTES
                        if (v.some(x => x && typeof x === 'object' && !Array.isArray(x))) {
                            const lotes = v.map(normalizeLoteObj).filter(Boolean);
                            const ids = lotes.map(x => asTrim(x.ID)).filter(Boolean);
                            return { kml, lotes, ids };
                        }
                        // primitivos => ids
                        const ids = v.map(x => asTrim(x)).filter(Boolean);
                        const lotes = ids.map(id => ({ ID: id, LOTE: '', N_FINCA: '', C_FINCA: '', TERCIO: '', ZAFRA: '' }));
                        return { kml, lotes, ids };
                    }

                    // v es objeto tipo lote
                    if (v && typeof v === 'object') {
                        const lote = normalizeLoteObj(v);
                        const lotes = lote ? [lote] : [];
                        const ids = lotes.map(x => asTrim(x.ID)).filter(Boolean);
                        return { kml, lotes, ids };
                    }

                    // v es primitivo (id)
                    const id = asTrim(v);
                    const lotes = id ? [{ ID: id, LOTE: '', N_FINCA: '', C_FINCA: '', TERCIO: '', ZAFRA: '' }] : [];
                    const ids = id ? [id] : [];
                    return { kml, lotes, ids };
                });

                out.sort((a, b) => Number(a.kml) - Number(b.kml));
                return out;
            }

            return [];
        };

        const buildListadoIdsLoteKmlsTexto = (arr) => {
            if (!Array.isArray(arr) || !arr.length) return '';

            return arr
                .map(({ kml, lotes }) => {
                    if (!Array.isArray(lotes) || !lotes.length) return `${kml}:`;

                    const lotesTxt = lotes.map(l => {
                        const parts = [];
                        if (l.ID) parts.push(`ID: ${l.ID}`);
                        if (l.LOTE) parts.push(`LOTE: ${l.LOTE}`);
                        if (l.N_FINCA) parts.push(`N. FINCA: ${l.N_FINCA}`);
                        if (l.C_FINCA) parts.push(`C.FINCA: ${l.C_FINCA}`);
                        if (l.TERCIO) parts.push(`TERCIO: ${l.TERCIO}`);
                        if (l.ZAFRA) parts.push(`ZAFRA: ${l.ZAFRA}`);
                        return parts.join(' · ');
                    }).join(' | ');

                    return `${kml}: ${lotesTxt}`;
                })
                .join(', ');
        };

        const unidadesRaw = (data && (data.unidades || data.UNIDADES)) || {};
        const unidades = (unidadesRaw && typeof unidadesRaw === 'object') ? unidadesRaw : {};

        const getUnidad = (campo) => {
            if (!unidades || typeof unidades !== 'object') return '';
            const key = String(campo || '').toUpperCase();
            const val = unidades[key];
            return typeof val === 'string' && val.trim() !== '' ? val.trim() : '';
        };

        // 🔹 Unidades dinámicas provenientes del backend
        const unidadVelocidad       = getUnidad("VELOCIDAD")        || "m/s";
        const unidadAltura          = getUnidad("ALTURA")           || "m";
        const unidadDosisReal       = getUnidad("DOSIS_REAL")       || "L/ha";
        const unidadDosisTeorica    = getUnidad("DOSIS_TEORICA")    || "L/ha";
        const unidadVolumenMezcla   = getUnidad("VOLUMEN_MEZCLA")   || "L";
        const unidadDiferenciaDosis = getUnidad("DIFERENCIA_DOSIS") || "L/ha";

        const unidadVolumenAgua     = getUnidad("VOLUMEN_AGUA")     || getUnidad("VOLUMEN_DE_AGUA")     || "L";
        const unidadDosisProducto    = getUnidad("DOSIS_PRODUCTO")  || getUnidad("DOSIS_DE_PRODUCTO")  || "L/ha";

        const formatWithUnidad = (campoUnidad, valor, decimals = 2, defaultUnidad = '') => {
            if (valor == null || valor === '') return '';
            const numero = safeNumber(valor, decimals);
            if (numero === '') return '';

            const unidadDinamica = getUnidad(campoUnidad);
            const unidad = unidadDinamica || defaultUnidad;

            return unidad ? `${numero} ${unidad}` : numero;
        };

        // ✅ Obtener y normalizar el listado por KML (IDs + props)
        const listadoIdsLoteKmlsRaw =
            (data && (
                data.listadoIdsLoteKmls ??
                data.LISTA_KMLS_IDS_LOTE ??
                data.LISTA_KMLS_IDS_LOTES ?? // 👈 soporte plural
                data.listaKmlsIdsLote ??
                data.LISTA_IDS_LOTE_KMLS
            )) ?? [];

        const listadoIdsLoteKmls = normalizeListadoIdsLoteKmls(listadoIdsLoteKmlsRaw);
        const listadoIdsLoteKmlsTexto = buildListadoIdsLoteKmlsTexto(listadoIdsLoteKmls);

        // ✅ Para evitar errores si LISTA_KMLS viene como string
        const listaKmlsArr = Array.isArray(data.LISTA_KMLS ?? data.listadoKmls)
            ? (data.LISTA_KMLS ?? data.listadoKmls)
            : [];

        // ✅ Arrays raw por vuelo (KML) para poder filtrar gráficas sin re-parsing
        const alturasRawArr = Array.isArray(data.ALTURAS ?? data.alturas)
            ? (data.ALTURAS ?? data.alturas)
            : [];

        const velocidadesRawArr = Array.isArray(data.VELOCIDADES ?? data.velocidades)
            ? (data.VELOCIDADES ?? data.velocidades)
            : [];

        const dosisRealesRawArr = Array.isArray(data.DOSIS_REALES ?? data.dosisReales)
            ? (data.DOSIS_REALES ?? data.dosisReales)
            : [];

        const dosisAreasRawArr = Array.isArray(data.DOSIS_AREAS ?? data.dosisAreas)
            ? (data.DOSIS_AREAS ?? data.dosisAreas)
            : [];

        const tipoSubAnalisisValor = data.TIPO_SUB_ANALISIS || data.tipoSubAnalisis || '';
        const isTasaVariable = String(tipoSubAnalisisValor).trim().toUpperCase() === 'TASA_VARIABLE';
        const hasMetricValue = (value) =>
            value !== undefined && value !== null && String(value).trim() !== '';
        const firstMetricValue = (...values) => {
            for (const value of values) {
                if (hasMetricValue(value)) return value;
            }
            return '';
        };
        const porcentajeVariacionDosisRaw = firstMetricValue(
            data.PORCENTAJE_VARIACION_DOSIS,
            data.porcentajeVariacionDosis,
            data.PORCENTAJE_VOLUMEN_REAL_VS_TEORICO,
            data.porcentajeVolumenRealVsTeorico,
            data.PORCENTAJE_VARIACION,
            data.porcentajeVariacion
        );
        const porcentajeVariacionRaw = isTasaVariable
            ? porcentajeVariacionDosisRaw
            : firstMetricValue(data.PORCENTAJE_VARIACION, data.porcentajeVariacion);
        const parseMetricNumber = (value) => {
            if (!hasMetricValue(value)) return Number.NaN;
            const match = String(value).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
            return match ? Number(match[0]) : Number.NaN;
        };
        const dosisRealVraRaw = firstMetricValue(
            data.DOSIS_REAL_PROMEDIO, data.dosisRealPromedio,
            data.DOSIS_REAL, data.dosisReal
        );
        const dosisTeoricaVraRaw = firstMetricValue(
            data.DOSIS_PRESCRIPCION_PROMEDIO, data.dosisPrescripcionPromedio,
            data.DOSIS_TEORICA, data.dosisTeorica,
            data.DOSIS_OBJETIVO_PROMEDIO, data.dosisObjetivoPromedio
        );
        const dosisRealVraNumber = parseMetricNumber(dosisRealVraRaw);
        const dosisTeoricaVraNumber = parseMetricNumber(dosisTeoricaVraRaw);
        const diferenciaVraCalculada = Number.isFinite(dosisRealVraNumber) && Number.isFinite(dosisTeoricaVraNumber)
            ? Math.abs(dosisTeoricaVraNumber - dosisRealVraNumber)
            : '';
        const diferenciaDosisVraRaw = firstMetricValue(
            diferenciaVraCalculada,
            data.DIFERENCIA_DOSIS,
            data.diferenciaDosisRealTeorica
        );

        const datos = {
            tipoSubAnalisis: {
                titulo: "Tipo de subanálisis",
                valor: tipoSubAnalisisValor
            },
            nombreResponsable: {
                titulo: "Nombre del Responsable",
                valor: data.RESPONSABLE || data.nombreResponsable || ''
            },
            responsablesLista: {
                titulo: "Responsables",
                valor: Array.isArray(data.responsablesLista)
                    ? data.responsablesLista
                    : (data.RESPONSABLE || data.nombreResponsable ? [data.RESPONSABLE || data.nombreResponsable] : [])
            },
            fechaInicio: {
                titulo: "Fecha de Inicio Actividad",
                valor: data.FECHA_INICIO || data.fechaInicio || ''
            },
            fechaFin: {
                titulo: "Fecha de Fin Actividad",
                valor: data.FECHA_FINAL || data.fechaFin || ''
            },
            nombreFinca: {
                titulo: "Nombre de la Finca",
                valor: data.NOMBRE_FINCA || data.nombreFinca || ''
            },
            codigoFinca: {
                titulo: "Código de la Finca",
                valor: data.CODIGO_FINCA || data.codigoFinca || ''
            },
            codigoLote: {
                titulo: "Código del Lote",
                valor: data.LOTE || data.codigoLote || ''
            },
            nombreOperador: {
                titulo: "Nombre del Operador",
                valor: data.NOMBRE_DE_OPERADOR || data.nombreOperador || ''
            },
            codigoEquipo: {
                titulo: "Código del Equipo",
                valor: data.CODIGO_DE_MAQUINA || data.codigoEquipo || ''
            },
            equiposLista: {
                titulo: "Equipos",
                valor: Array.isArray(data.equiposLista)
                    ? data.equiposLista
                    : (
                        data.EQUIPO
                            ? [data.EQUIPO]
                            : (data.CODIGO_DE_MAQUINA || data.codigoEquipo ? [data.CODIGO_DE_MAQUINA || data.codigoEquipo] : [])
                    )
            },
            horaInicio: {
                titulo: "Hora de Inicio",
                valor: data.HORA_INICIO || data.horaInicio || ''
            },
            horaFin: {
                titulo: "Hora de Finalización",
                valor: data.HORA_FIN || data.horaFin || ''
            },
            eficiencia: {
                titulo: "Eficiencia",
                valor: data.EFICIENCIA != null
                    ? formatWithUnidad("EFICIENCIA", data.EFICIENCIA ?? data.eficiencia, 2, "%")
                    : ''
            },
            dosisTeorica: {
                titulo: "Volumen Teorico",
                valor: formatWithUnidad(
                    "DOSIS_TEORICA",
                    isTasaVariable ? dosisTeoricaVraRaw : (data.DOSIS_TEORICA ?? data.dosisTeorica),
                    2,
                    unidadDosisTeorica
                )
            },
            volumenAgua: {
                titulo: "Volumen de Agua",
                valor: formatWithUnidad(
                    "VOLUMEN_AGUA",
                    data.VOLUMEN_DE_AGUA_L ?? data.volumenAguaL ?? data.VOLUMEN_DE_AGUA ?? data.volumenAgua,
                    3,
                    unidadVolumenAgua
                )
            },
            dosisProducto: {
                titulo: "Dosis de Producto",
                valor: formatWithUnidad(
                    "DOSIS_PRODUCTO",
                    data.DOSIS_DE_PRODUCTO_LHA ?? data.dosisProductoLHa ?? data.DOSIS_DE_PRODUCTO ?? data.dosisProducto,
                    3,
                    unidadDosisProducto
                )
            },
            humedadDelCultivo: {
                titulo: "Humedad del Cultivo",
                valor: formatWithUnidad("HUMEDAD_DEL_CULTIVO", data.HUMEDAD_DEL_CULTIVO ?? data.humedadDelCultivo, 2, "%")
            },
            tchEstimado: {
                titulo: "TCH Estimado",
                valor: safeNumber(data.TCH_ESTIMADO ?? data.tchEstimado)
            },
            tiempoTotal: {
                titulo: "Tiempo Total",
                valor: data.TIEMPO_TOTAL || data.tiempoTotal || ''
            },
            productoAps: {
                titulo: "Producto",
                valor: data.PRODUCTO || data.productoAps || ''
            },
            productosLista: {
                titulo: "Productos",
                valor: Array.isArray(data.productosLista)
                    ? data.productosLista
                    : ((data.PRODUCTO || data.productoAps) ? [data.PRODUCTO || data.productoAps] : [])
            },

            alturaAps: {
                titulo: "Altura Promedio",
                valor: formatWithUnidad("ALTURA", data.ALTURA ?? data.alturaPromedio, 2, unidadAltura)
            },

            alturas: {
                titulo: "Alturas (ALTURA) por KML",
                valor: safeArray(data.ALTURAS ?? data.alturas),
                raw: alturasRawArr
            },

            velocidadAps: {
                titulo: "Velocidad Promedio",
                valor: formatWithUnidad("VELOCIDAD", data.VELOCIDAD ?? data.velocidad, 2, unidadVelocidad)
            },
            velocidades: {
                titulo: "Velocidades por vuelo",
                valor: safeArray(data.VELOCIDADES ?? data.velocidades),
                raw: velocidadesRawArr
            },

            dosisReal: {
                titulo: "Volumen Real",
                valor: formatWithUnidad(
                    "DOSIS_REAL",
                    isTasaVariable ? dosisRealVraRaw : (data.DOSIS_REAL ?? data.dosisReal),
                    2,
                    unidadDosisReal
                )
            },
            dosisReales: {
                titulo: "Volumenes Reales (por KML)",
                valor: safeArray(data.DOSIS_REALES ?? data.dosisReales),
                raw: dosisRealesRawArr
            },
            dosisAreas: {
                titulo: "Volumenes Áreas (por KML)",
                valor: safeArray(data.DOSIS_AREAS ?? data.dosisAreas),
                raw: dosisAreasRawArr
            },

            volumenPromedio: {
                titulo: "Volumen Promedio Mezcla",
                valor: formatWithUnidad("VOLUMEN_MEZCLA", data.VOLUMEN_PROMEDIO ?? data.volumenPromedio, 2, unidadVolumenMezcla)
            },

            porcentajeVariacion: {
                titulo: "Porcentaje de Variación Dosis",
                valor: formatWithUnidad("PORCENTAJE_VARIACION", porcentajeVariacionRaw, 2, "%")
            },
            porcentajeVariacionDosis: {
                titulo: "Porcentaje de variación dosis VAR",
                valor: formatWithUnidad("PORCENTAJE_VARIACION_DOSIS", porcentajeVariacionDosisRaw, 2, "%")
            },
            numeroKmls: {
                titulo: "Número de KMLs",
                valor: Array.isArray(listaKmlsArr) && listaKmlsArr.length
                    ? listaKmlsArr.length
                    : (data.numeroKmls ?? ''),
                raw: Array.isArray(listaKmlsArr) ? listaKmlsArr.length : 0
            },
            listadoKmls: {
                titulo: "Listado de KMLs",
                valor: Array.isArray(listaKmlsArr)
                    ? listaKmlsArr.join(', ')
                    : '',
                raw: Array.isArray(listaKmlsArr) ? listaKmlsArr : []
            },

            // ✅ NUEVO: listado estructurado KML -> LOTES/IDs (con props)
            listadoIdsLoteKmls: {
                titulo: "IDs/Lotes por KML",
                valor: listadoIdsLoteKmls
            },

            // ✅ NUEVO: versión en texto para UI rápida
            listadoIdsLoteKmlsTexto: {
                titulo: "IDs/Lotes por KML (texto)",
                valor: listadoIdsLoteKmlsTexto
            },

            lotesLista: {
                titulo: "Lotes",
                valor: Array.isArray(data.lotesLista)
                    ? data.lotesLista.map(x => String(x))
                    : (data.LOTE ? [String(data.LOTE)] : [])
            },

            // 🔹 LISTAS NUMÉRICAS POR LOTE
            velocidadesLista: {
                titulo: `Velocidad por Lote (${unidadVelocidad})`,
                valor: Array.isArray(data.VELOCIDADES_LISTA ?? data.velocidadesLista ?? data.VELOCIDADES ?? data.velocidades)
                    ? (data.VELOCIDADES_LISTA ?? data.velocidadesLista ?? data.VELOCIDADES ?? data.velocidades).map(v => Number(v))
                    : []
            },
            alturasLista: {
                titulo: `Altura por Lote (${unidadAltura})`,
                valor: Array.isArray(data.alturasLista ?? data.ALTURAS)
                    ? (data.alturasLista ?? data.ALTURAS).map(v => Number(v))
                    : []
            },
            dosisRealesLista: {
                titulo: `Volumen Real por Lote (${unidadDosisReal})`,
                valor: Array.isArray(data.dosisRealesLista)
                    ? data.dosisRealesLista.map(v => Number(v))
                    : []
            },
            humedadesLista: {
                titulo: "Humedad por Lote (%)",
                valor: Array.isArray(data.humedadesLista)
                    ? data.humedadesLista.map(v => Number(v))
                    : []
            },
            tchEstimadoLista: {
                titulo: "TCH por Lote (/ha)",
                valor: Array.isArray(data.tchEstimadoLista)
                    ? data.tchEstimadoLista.map(v => Number(v))
                    : []
            },
            dosisTeoricasLista: {
                titulo: `Volumen Teórico por Lote (${unidadDosisTeorica})`,
                valor: Array.isArray(data.dosisTeoricasLista)
                    ? data.dosisTeoricasLista.map(v => Number(v))
                    : []
            },
            diferenciaDosisTeoricaReal: {
                titulo: "Dif. volumen T vs R",
                valor: formatWithUnidad(
                    "DIFERENCIA_DOSIS",
                    isTasaVariable ? diferenciaDosisVraRaw : (data.DIFERENCIA_DOSIS ?? data.diferenciaDosisRealTeorica),
                    3,
                    unidadDiferenciaDosis
                )
            },

            // 🔹 Guardamos las unidades explícitamente
            unidadVelocidad: { titulo: "Unidad Velocidad", valor: unidadVelocidad },
            unidadAltura: { titulo: "Unidad Altura", valor: unidadAltura },
            unidadDosisReal: { titulo: "Unidad Dosis Real", valor: unidadDosisReal },
            unidadDosisTeorica: { titulo: "Unidad Dosis Teórica", valor: unidadDosisTeorica },
            unidadVolumenMezcla: { titulo: "Unidad Volumen Mezcla", valor: unidadVolumenMezcla },
            unidadVolumenAgua: { titulo: "Unidad Volumen Agua", valor: unidadVolumenAgua },
            unidadDosisProducto: { titulo: "Unidad Dosis Producto", valor: unidadDosisProducto },
            unidadDiferenciaDosis: { titulo: "Unidad Diferencia Dosis", valor: unidadDiferenciaDosis }
        };

        return datos;
    }
};

export const fetchDataAps = async (idAnalisisAps, setDatosAnalisis) => {
    try {
        const data = await obtenerDatosCompletosAps(idAnalisisAps);
        console.log("ESTOS SON DATOS BACKEND APS:", data);
        setDatosAnalisis(buildApsDatosAnalisis(data));
    } catch (error) {
        console.error("Error al cargar datos de APS:", error);
        setDatosAnalisis({});
    }
};

export const shouldEnableExecBashAps = () => {
    try {
        const raw = localStorage.getItem('aplicacionesAereas_manual_rows') || '[]';
        const rows = JSON.parse(raw);
        return Array.isArray(rows) && rows.length > 0;
    } catch {
        return false;
    }
};

export const cargaDatosAps = async (userData, selectedAnalysisTypeRef) => {
    if (selectedAnalysisTypeRef.current && userData.userId) {
        try {
            const response = await ultimoAnalisis(selectedAnalysisTypeRef, userData.userId);
            if (response && response.data && response.data.ID_ANALISIS) {
                return response;
            } else {
                console.error("Respuesta del último análisis no contiene datos esperados");
            }
        } catch (error) {
            console.error("Error al obtener último análisis:", error);
        }
    }
};

/**
 * processApsData
 */
export const processApsData = (datosAnalisis, indicadores) => {
    const {
        alturas,
        velocidades,
        dosisAreas,
        dosisReales,
        listadoKmls,
        ...datosParaCards
    } = datosAnalisis || {};


    const getValor = (obj) =>
        obj && typeof obj === 'object' && 'valor' in obj ? obj.valor : obj;

    const toArray = (maybeArrayOrString) => {
        const v = getValor(maybeArrayOrString);
        if (Array.isArray(v)) return v.filter(x => String(x || '').trim() !== '');
        if (typeof v === 'string' && v.trim() !== '') return [v.trim()];
        return [];
    };

    const unidadVelocidad       = getValor(datosAnalisis?.unidadVelocidad)       || "m/s";
    const unidadAltura          = getValor(datosAnalisis?.unidadAltura)          || "m";
    const unidadDosisReal       = getValor(datosAnalisis?.unidadDosisReal)       || "L/ha";
    const unidadDosisTeorica    = getValor(datosAnalisis?.unidadDosisTeorica)    || "L/ha";
    const unidadVolumenMezcla   = getValor(datosAnalisis?.unidadVolumenMezcla)   || "L";
    const unidadDiferenciaDosis = getValor(datosAnalisis?.unidadDiferenciaDosis) || "L/ha";

    const productosLista =
        toArray(datosAnalisis?.productosLista)
            .concat(toArray(datosAnalisis?.productoAps))
            .filter((v, i, self) => self.indexOf(v) === i);

    const equiposLista =
        toArray(datosAnalisis?.equiposLista)
            .concat(
                toArray(datosAnalisis?.equipo),
                toArray(datosAnalisis?.codigoEquipo)
            )
            .filter((v, i, self) => self.indexOf(v) === i);

    const responsablesLista =
        toArray(datosAnalisis?.responsablesLista)
            .concat(toArray(datosAnalisis?.nombreResponsable))
            .filter((v, i, self) => v && self.indexOf(v) === i);

    const datosExtendidos = {
        ...datosParaCards,
        ...(productosLista.length ? { productosLista: { titulo: 'Productos', valor: productosLista } } : {}),
        ...(equiposLista.length ? { equiposLista: { titulo: 'Equipos', valor: equiposLista } } : {}),
        ...(responsablesLista.length ? { responsablesLista: { titulo: 'Responsables', valor: responsablesLista } } : {}),
    };

    const dosisRealValor = getValor(datosAnalisis?.dosisReal) || "";

    return {
        analisis: "APLICACIONES_AEREAS",
        ...datosExtendidos,
        indicadores: {
            promedioAltura: {
                titulo: "Promedio de Altura",
                valor: indicadores.promedioAltura ? `${indicadores.promedioAltura} ${unidadAltura}` : "",
            },
            areaSobreAplicada: {
                titulo: "Área Sobre Aplicada",
                valor: indicadores.areaSobreAplicada ? `${indicadores.areaSobreAplicada} ha` : "",
            },
            areaAplicada: {
                titulo: "Área Aplicada",
                valor: indicadores.areaAplicada ? `${indicadores.areaAplicada} ha` : "",
            },
            areaNoAplicada: {
                titulo: "Área No Aplicada",
                valor: indicadores.areaNoAplicada ? `${indicadores.areaNoAplicada} ha` : "",
            },
            areaAplicadaEnCalles: {
                titulo: "Área Aplicada en Calles",
                valor: indicadores.areaSobreCalles ? `${indicadores.areaSobreCalles} ha` : "",
            },
            areaAplicadaFueraDelLote: {
                titulo: "Área Aplicada Fuera del Lote",
                valor: indicadores.areaFueraDeLote ? `${indicadores.areaFueraDeLote} ha` : "",
            },
            porcentajeVariacion: {
                titulo: "Porcentaje de Variación",
                valor: indicadores.porcentajeVariacion,
            },
            porcentajeVariacionDosis: {
                titulo: "Porcentaje de variación dosis",
                valor: indicadores.porcentajeVariacionDosis,
            },
            promedioDosisReal: {
                titulo: "Promedio de Volumen Real",
                valor: indicadores.promedioDosisReal ? `${indicadores.promedioDosisReal} ${unidadDosisReal}` : "",
            },
            promedioVelocidad: {
                titulo: "Promedio de Velocidad",
                valor: indicadores.promedioVelocidad ? `${indicadores.promedioVelocidad} ${unidadVelocidad}` : "",
            },
            areaTotalLote: {
                titulo: "Área total del bloque",
                valor: indicadores.areaTotalLote ? `${indicadores.areaTotalLote} m` : "",
            },
            diferenciaDeAreaApsKml: {
                titulo: "Diferencia Volumen teorico y real",
                valor: indicadores.diferenciaDeAreaApsKml ? `${indicadores.diferenciaDeAreaApsKml} ha` : "",
            },
            dosisTotalRealAplicada: {
                titulo: "Volumen Real Mezcla",
                valor: dosisRealValor,
            },
        },
    };
};

/* ======================================================================
   ✅ FIX REAL: detectar loggedUserId de forma robusta y enviarlo siempre
   cuando userId=ALL (para que el backend resuelva empresa/scope).
   ====================================================================== */

const isMongoObjectId = (v) => typeof v === 'string' && /^[a-fA-F0-9]{24}$/.test(v.trim());

const base64UrlToString = (b64url) => {
    try {
        let s = String(b64url || '').replace(/-/g, '+').replace(/_/g, '/');
        while (s.length % 4 !== 0) s += '=';
        return window.atob(s);
    } catch {
        return null;
    }
};

const decodeJwtPayload = (token) => {
    const t = String(token || '').trim();
    const parts = t.split('.');
    if (parts.length !== 3) return null;
    const decoded = base64UrlToString(parts[1]);
    if (!decoded) return null;
    try {
        return JSON.parse(decoded);
    } catch {
        return null;
    }
};

const extractMongoIdDeep = (input, depth = 0) => {
    if (depth > 5 || input == null) return null;

    if (typeof input === 'string') {
        const s = input.trim();
        if (isMongoObjectId(s)) return s;
        return null;
    }

    if (Array.isArray(input)) {
        for (const it of input) {
            const found = extractMongoIdDeep(it, depth + 1);
            if (found) return found;
        }
        return null;
    }

    if (typeof input === 'object') {
        const preferredKeys = [
            'userId', 'USER_ID', 'ID_USUARIO', '_id', 'id', 'uid', 'sub',
            'loggedUserId', 'requestUserId', 'safeLoggedUserId'
        ];

        for (const k of preferredKeys) {
            if (k in input) {
                const found = extractMongoIdDeep(input[k], depth + 1);
                if (found) return found;
            }
        }

        const nestedKeys = ['user', 'data', 'session', 'auth', 'profile', 'currentUser'];
        for (const k of nestedKeys) {
            if (k in input) {
                const found = extractMongoIdDeep(input[k], depth + 1);
                if (found) return found;
            }
        }

        for (const k of Object.keys(input)) {
            const found = extractMongoIdDeep(input[k], depth + 1);
            if (found) return found;
        }
    }

    return null;
};

const safeJsonParse = (raw) => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const getLoggedUserIdFromStorage = () => {
    const storages = [window?.localStorage, window?.sessionStorage].filter(Boolean);

    for (const st of storages) {
        try {
            const commonKeys = ['userData', 'USER_DATA', 'auth', 'AUTH', 'user', 'USER', 'currentUser', 'CURRENT_USER', 'token', 'TOKEN'];
            for (const key of commonKeys) {
                const raw = st.getItem(key);
                if (!raw) continue;

                if (isMongoObjectId(raw.trim())) return raw.trim();

                const parsed = safeJsonParse(raw);
                if (parsed) {
                    const found = extractMongoIdDeep(parsed);
                    if (found) return found;
                }

                const jwt = decodeJwtPayload(raw);
                if (jwt) {
                    const found = extractMongoIdDeep(jwt);
                    if (found) return found;
                }
            }

            for (let i = 0; i < st.length; i++) {
                const key = st.key(i);
                if (!key) continue;

                const raw = st.getItem(key);
                if (!raw) continue;

                if (raw.length > 200000) continue;

                if (isMongoObjectId(raw.trim())) return raw.trim();

                const parsed = safeJsonParse(raw);
                if (parsed) {
                    const found = extractMongoIdDeep(parsed);
                    if (found) return found;
                }

                const jwt = decodeJwtPayload(raw);
                if (jwt) {
                    const found = extractMongoIdDeep(jwt);
                    if (found) return found;
                }
            }
        } catch {
            // ignore
        }
    }

    return null;
};

const normalizeLoggedUserId = (candidate) => {
    if (candidate == null) return null;
    if (typeof candidate === 'string') return isMongoObjectId(candidate.trim()) ? candidate.trim() : null;
    const found = extractMongoIdDeep(candidate);
    return found && isMongoObjectId(found) ? found : null;
};

export const fetchDataApsIndicators = async (startDate, endDate, endpoint, idUsuario, minMaxParams, equipo, filters = {}, { signal } = {}) => {
    try {
        const loggedFromFilters =
            normalizeLoggedUserId(filters.loggedUserId) ||
            normalizeLoggedUserId(filters.requestUserId) ||
            normalizeLoggedUserId(filters.safeLoggedUserId) ||
            normalizeLoggedUserId(filters.userData) ||
            normalizeLoggedUserId(filters.user) ||
            normalizeLoggedUserId(filters.auth) ||
            normalizeLoggedUserId(filters.session) ||
            normalizeLoggedUserId(filters.currentUser);

        const loggedFromStorage = getLoggedUserIdFromStorage();

        const loggedUserId =
            loggedFromFilters ||
            (loggedFromStorage && isMongoObjectId(loggedFromStorage) ? loggedFromStorage : null) ||
            (typeof idUsuario === 'string' && isMongoObjectId(idUsuario) ? idUsuario : null);

        const params = new URLSearchParams();

        params.set('FECHA_INICIO', startDate);
        params.set('FECHA_FINAL', endDate);
        params.set('userId', idUsuario ?? '');
        params.set('minMax', JSON.stringify(minMaxParams ?? {}));
        params.set('EQUIPO', equipo ?? '');

        params.set('NOMBRE_DE_OPERADOR', filters.operadorAA || '');
        params.set('NOMBRE_FINCA', filters.fincaAA || '');
        params.set('PRODUCTO', filters.productoAA || '');
        params.set('ZAFRA', filters.zafraAA || '');
        params.set('TERCIO', filters.tercioAA || '');
        params.set('LOTE', filters.loteAA || '');
        params.set('FILTER_USER_IDS', filters.FILTER_USER_IDS || '');
        params.set('FORMULACION', filters.tipoProductoAA || filters.FORMULACION || '');

        if (loggedUserId) {
            params.set('loggedUserId', loggedUserId);
            params.set('requestUserId', loggedUserId);
            params.set('safeLoggedUserId', loggedUserId);
        } else if (String(idUsuario || '').toUpperCase() === 'ALL') {
            console.warn('[APS] userId=ALL pero no se pudo resolver loggedUserId. Revisa dónde guardas userData/token.');
        }

        const url = `${API_BASE_URL_DASHBOARD}api/indicators/aplicaciones-aereas/${endpoint}?${params.toString()}`;
        const response = await fetch(url, { signal });

        let data;
        try {
            data = await response.json();
        } catch (e) {
            console.error("Respuesta no es JSON válido en APS:", e);
            return [];
        }

        if (!response.ok) {
            console.error("Error HTTP en APS Indicators:", response.status, data);
            return [];
        }

        const arr = Array.isArray(data) ? data : (data && typeof data === 'object' ? [data] : []);

        return arr.map(item => {
            const indicadores = {
                _id: item?._id ?? null
            };

            Object.keys(item || {}).forEach(key => {
                const indicador = item[key];

                if (indicador && typeof indicador === 'object' && 'valor' in indicador && 'titulo' in indicador) {
                    indicadores[key] = {
                        title: indicador.titulo,
                        value: indicador.valor
                    };
                }
            });

            return indicadores;
        });
    } catch (error) {
        console.error("Error al obtener los indicadores de aplicaciones aéreas:", error);
        return [];
    }
};

export const initialIndicatorsAps = {
    cardData: {
        velocidad: { title: 'Velocidad', max: '', min: '' },
        dosisReal: { title: 'Volumen Real', max: '', min: '' },
        dosisTeorica: { title: 'Volumen Teorica', max: '', min: '' },
        areaAplicada: { title: 'Área aplicada', max: '', min: '' },
        areaNoAplicada: { title: 'Área no aplicada', max: '', min: '' },
        areaSobreAplicada: { title: 'Área sobre aplicada', max: '', min: '' },
        tch: { title: 'TCH', max: '', min: '' },
        altura: { title: 'Altura', max: '', min: '' },
    }
};
